import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import './Vanilla';

// external library
import { register } from 'swiper/element/bundle';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import NavigationDesktop from './Components/NavigationDesktop';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DealerInfoContainer from './Components/DealerInfo/DealerInfoContainer';
import DealerSinglePageContainer from './Components/DealerInfo/DealerSinglePageContainer';
import FindMyPartContainer from './Components/FindMyPart/FindMyPartContainer';
import MiniFindMyPartContainer from './Components/MiniFindMyPart/MiniFindMyPartContainer';
import DynamicComponent from './Components/DynamicComponent';
import LanguageSelector from './Components/LanguageSelector';
import LanguageSwitch from './Components/LanguageSwitch';
import Library from './Components/Library';
import LibrarySearch from './Components/LibrarySearch';

import { CompareCloseButton } from './Components/CompareButton';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

const getSiblings = (ele) => {
  return [].slice
    .call(ele.parentNode.children)
    .filter((child) => child !== ele);
};

const toggleTopLink = (e) => {
  let targetLinkEl = e.target;
  if (targetLinkEl) {
    if (targetLinkEl.tagName.toLowerCase() !== 'a') {
      targetLinkEl = targetLinkEl.parentNode;
    }
    const parentEl = targetLinkEl.parentNode;
    if (parentEl && parentEl.classList.contains('has-children')) {
      e.preventDefault();
      if (parentEl.classList.contains('expanded')) {
        parentEl.classList.remove('expanded');
        return;
      }

      //const siblings = getSiblings(parentEl);
      parentEl.classList.add('expanded');
    }
  }
};

document.addEventListener(
  'DOMContentLoaded',
  function () {
    const subnavExpendables = document.querySelectorAll(
      'nav.subnav > .subnav__list > .subnav__item > .subnav__link'
    );

    if (subnavExpendables) {
      Array.from(subnavExpendables).forEach((expandable, index) => {
        expandable.addEventListener('click', toggleTopLink, false);
      });
    }
  },
  false
);

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  if (document.getElementById('toast')) {
    renderReact(
      <Provider store={store}>
        <ToastContainer
          position="top-right"
          style={{ top: '128px' }}
          icon={false}
          className="toastify"
          closeButton={CompareCloseButton}
          progressStyle={{ height: '4px' }}
          newestOnTop
        />
      </Provider>,
      document.getElementById('toast')
    );
  }

  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCart />
      </Provider>,
      document.getElementById('miniCart')
    );
  }
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={store}>
        <QuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }
  if (document.getElementById('newsList')) {
    const NewsList = DynamicComponent({
      loader: () => import('./Components/News/NewsList'),
    });
    renderReact(<NewsList />, document.getElementById('newsList'));
  }
  const navbar = document.getElementById('navbar');
  if (navbar) {
    const { itemType } = navbar.dataset;
    renderReact(
      <Provider store={store}>
        <Navigation {...{ itemType }} />
      </Provider>,
      navbar
    );
  }
  const navbarDesktop = document.getElementById('navbar-desktop');
  if (navbarDesktop) {
    renderReact(
      <Provider store={store}>
        <NavigationDesktop />
      </Provider>,
      navbarDesktop
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }

  // Quick order page
  const quickOrderEl = document.getElementById('quick-order');
  if (quickOrderEl) {
    const QuickOrder = DynamicComponent({
      loader: () => import('./Components/QuickOrder'),
    });
    renderReact(
      <Provider store={store}>
        <QuickOrder {...quickOrderEl.dataset} />
      </Provider>,
      quickOrderEl
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  const springCalcContainer = document.querySelectorAll('spring-calc-button');
  if (springCalcContainer) {
    const SpringCalcContainer = DynamicComponent({
      loader: () => import('./Components/SpringCalcContainer'),
    });
    Array.from(springCalcContainer).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        selectedVariant,
        selectedVariantId,
        href,
        imageUrl,
        partNo,
        price,
        cssClass,
        iconClass,
        label,
        brand,
        model,
        year,
        isCalculatorInTab,
      } = button.dataset;

      renderReact(
        <Provider store={store}>
          <SpringCalcContainer
            {...{
              label,
              articleNumber,
              quantityFieldId,
              selectedVariant,
              selectedVariantId,
              href,
              imageUrl,
              partNo,
              price,
              cssClass,
              iconClass,
              brand,
              model,
              year,
              isCalculatorInTab: false,
              isCartridgeKit: !!('cartridgeKit' in button.dataset),
            }}
          />
        </Provider>,
        button
      );
    });
  }
  const springCalcAreas = document.querySelectorAll('.spring-calculator-area');
  if (springCalcAreas) {
    const SpringCalcArea = DynamicComponent({
      loader: () => import('./Components/SpringCalcArea'),
    });
    Array.from(springCalcAreas).forEach((springCalcArea) => {
      const {
        articleNumber,
        quantityFieldId,
        selectedVariant,
        selectedVariantId,
        href,
        imageUrl,
        partNo,
        price,
        cssClass,
        iconClass,
        label,
        brand,
        model,
        year,
      } = springCalcArea.dataset;
      const tempIsCalculatorInTab = springCalcArea.dataset.isCalculatorInTab;
      const isCalculatorInTab =
        tempIsCalculatorInTab && tempIsCalculatorInTab === 'true';
      renderReact(
        <Provider store={store}>
          <SpringCalcArea
            {...{
              label,
              articleNumber,
              quantityFieldId,
              selectedVariant,
              selectedVariantId,
              href,
              imageUrl,
              partNo,
              price,
              cssClass,
              iconClass,
              brand,
              model,
              year,
              isCalculatorInTab,
              isCartridgeKit: !!('cartridgeKit' in springCalcArea.dataset),
            }}
          />
        </Provider>,
        springCalcArea
      );
    });
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        iconClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
              iconClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }

  if (document.querySelectorAll('compare-button').length > 0) {
    const CompareButton = DynamicComponent({
      loader: () => import('./Components/CompareButton'),
    });
    Array.from(document.querySelectorAll('compare-button')).forEach(
      (button) => {
        const { label, articleNumber } = button.dataset;
        renderReact(
          <Provider store={store}>
            <CompareButton {...{ label, articleNumber }} />
          </Provider>,
          button
        );
      }
    );
  }

  if (document.getElementById('compare-footer')) {
    const CompareFooter = DynamicComponent({
      loader: () => import('./Components/CompareFooter'),
    });
    renderReact(
      <Provider store={store}>
        <CompareFooter />
      </Provider>,
      document.getElementById('compare-footer')
    );
  }

  const productCompare = document.getElementById('product-compare');
  if (productCompare) {
    const ComparePageContainer = DynamicComponent({
      loader: () => import('./Components/ComparePage/ComparePageContainer'),
    });

    const { title } = productCompare.dataset;
    renderReact(
      <Provider store={store}>
        <ComparePageContainer {...{ title }} />
      </Provider>,
      productCompare
    );
  }

  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }

  if (document.getElementById('orderHistoryTabBadge')) {
    const OrderHistoryTabBadge = DynamicComponent({
      loader: () => import('./Components/OrderHistoryTabBadge'),
    });
    renderReact(
      <Provider store={store}>
        <OrderHistoryTabBadge />
      </Provider>,
      document.getElementById('orderHistoryTabBadge')
    );
  }

  if (document.getElementById('accountOrdersPage')) {
    const AccountOrders = DynamicComponent({
      loader: () => import('./Components/AccountOrders/AccountOrdersContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AccountOrders />
      </Provider>,
      document.getElementById('accountOrdersPage')
    );
  }

  // My Pages tab URL handling
  const myPagesTabRadios = document.querySelectorAll('.tab__radio');
  if (myPagesTabRadios.length > 0) {
    const TAB_PARAM = 'tab';

    const getTabFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      return params.get(TAB_PARAM);
    };

    const updateTabUrl = (tabId) => {
      const url = new URL(window.location.href);
      if (tabId) {
        url.searchParams.set(TAB_PARAM, tabId);
      } else {
        url.searchParams.delete(TAB_PARAM);
      }
      // Keep order param if present
      window.history.pushState({ tab: tabId }, '', url.toString());
    };

    const selectTab = (tabId) => {
      const radio = document.getElementById(tabId);
      if (radio) {
        radio.checked = true;
      }
    };

    // On page load, select tab from URL
    const urlTab = getTabFromUrl();
    if (urlTab) {
      selectTab(urlTab);
    }

    // Update URL when tab is clicked
    myPagesTabRadios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          updateTabUrl(e.target.id);
        }
      });
    });

    // Handle browser back/forward for tabs
    window.addEventListener('popstate', (event) => {
      const tabId = event.state?.tab || getTabFromUrl();
      if (tabId) {
        selectTab(tabId);
      }
    });
  }

  // Dealer page (Map, filters, info)
  const dealerInfoEl = document.getElementById('dealerInfo');
  if (dealerInfoEl) {
    const { startLatitude, startLongitude, startZoom } = dealerInfoEl.dataset;
    renderReact(
      <Provider store={store}>
        <DealerInfoContainer
          startLatitude={startLatitude}
          startLongitude={startLongitude}
          startZoom={startZoom}
        />
      </Provider>,
      dealerInfoEl
    );
  }

  // Dealer page (Map, filters, info)
  const dealerSinglePageEl = document.getElementById('dealerPage');

  if (dealerSinglePageEl) {
    const { latitude, longitude } = dealerSinglePageEl.dataset;
    renderReact(
      <Provider store={store}>
        <DealerSinglePageContainer latitude={latitude} longitude={longitude} />
      </Provider>,
      dealerSinglePageEl
    );
  }
  const findMyPartBlockEl = document.getElementById('findMyPartBlock');
  if (findMyPartBlockEl) {
    const {
      useH1Title,
      title,
      description,
      preSetBrand,
      preSetModel,
      preSetYear,
      preSetSegment,
      ignoreSetCookie,
    } = findMyPartBlockEl.dataset;
    renderReact(
      <Provider store={store}>
        <FindMyPartContainer
          {...{
            preSetBrand,
            preSetModel,
            preSetYear,
            preSetSegment,
            title,
            description,
            isInBlock: true,
          }}
          useH1Title={useH1Title === 'true'}
          ignoreSetCookie={ignoreSetCookie?.toLowerCase() == 'true'}
        />
      </Provider>,
      findMyPartBlockEl
    );
  }

  const findMyPartEl = document.getElementById('findMyPart');
  if (findMyPartEl) {
    const {
      useH1Title,
      title,
      description,
      preSetBrand,
      preSetModel,
      preSetYear,
      preSetSegment,
      ignoreSetCookie,
      isSearch,
    } = findMyPartEl.dataset;
    renderReact(
      <Provider store={store}>
        <FindMyPartContainer
          {...{
            preSetBrand,
            preSetModel,
            preSetYear,
            preSetSegment,
            title,
            description,
            isInBlock: false,
            isSearch: isSearch && isSearch === 'true',
          }}
          ignoreSetCookie={ignoreSetCookie?.toLowerCase() == 'true'}
        />
      </Provider>,
      findMyPartEl
    );
  }

  const miniFindMyPartEl = document.getElementById('productDetailFindMyPart');
  if (miniFindMyPartEl) {
    const { baseProductSystemId, selectedVariantId } = miniFindMyPartEl.dataset;
    renderReact(
      <Provider store={store}>
        <MiniFindMyPartContainer
          baseProductSystemId={baseProductSystemId}
          selectedVariantId={selectedVariantId}
        />
      </Provider>,
      miniFindMyPartEl
    );
  }
  const languageSelectorEl = document.getElementById('languageSelector');
  if (languageSelectorEl) {
    const { title } = languageSelectorEl.dataset;
    renderReact(
      <Provider store={store}>
        <LanguageSelector title={title} />
      </Provider>,
      languageSelectorEl
    );
  }

  const languageSwitchEl = document.getElementById('languageSwitch');
  if (languageSwitchEl) {
    const dataEl = languageSwitchEl.querySelector('script[type="application/json"]');
    let items = [];
    try {
      items = JSON.parse(dataEl ? dataEl.textContent : '[]');
    } catch (e) {
      items = [];
    }
    renderReact(<LanguageSwitch items={items} />, languageSwitchEl);
  }

  if (document.getElementById('librarySearch')) {
    renderReact(
      <Provider store={store}>
        <LibrarySearch />
      </Provider>,
      document.getElementById('librarySearch')
    );
  }

  if (document.getElementById('library')) {
    renderReact(
      <Provider store={store}>
        <Library />
      </Provider>,
      document.getElementById('library')
    );
  }

  const footerMenuColumns = document.querySelectorAll(
    '[data-component="footer-menu-column"]'
  );
  if (!!footerMenuColumns.length) {
    const FooterMenuColumn = DynamicComponent({
      loader: () => import('./Components/FooterMenuColumn'),
    });
    Array.from(footerMenuColumns).forEach((container) => {
      try {
        const jsonDataEl = container.querySelector('json-data');
        if (!jsonDataEl) return;
        const data = JSON.parse(jsonDataEl.innerHTML);

        renderReact(
          <Provider store={store}>
            <FooterMenuColumn {...data} />
          </Provider>,
          container
        );
      } catch (error) {
        console.error(error);
      }
    });
  }
};

bootstrapComponents();
// register Swiper custom elements
register();
